import React, { useEffect } from "react";
import axios from "axios";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Navigation } from "./components/Navigation";
import { Footer } from "./components/Footer";
import { Toaster } from "./components/ui/sonner";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";
import Home from "./pages/Home";
import About from "./pages/About";
import Gallery from "./pages/Gallery";
import Reviews from "./pages/Reviews";
import Contact from "./pages/Contact";
import Quote from "./pages/Quote";
import AdminDashboard from "./pages/AdminDashboard";
import { OpsAuthGuard } from "./ops/OpsAuthGuard";
import OpsLogin from "./ops/OpsLogin";
import { OpsLayout } from "./ops/OpsLayout";
import { OpsDashboard } from "./ops/pages/OpsDashboard";
import { OpsCalendar } from "./ops/pages/OpsCalendar";
import { OpsProjects } from "./ops/pages/OpsProjects";
import { OpsProjectWorkspace } from "./ops/pages/OpsProjectWorkspace";
import { OpsPlaceholder } from "./ops/pages/OpsPlaceholder";
import OpsEmployeePay from "./ops/pages/OpsEmployeePay";
import EmployeeLogin from "./employee/pages/EmployeeLogin";
import WorkforceLanding from "./employee/pages/WorkforceLanding";
import EmployeeJoin from "./employee/pages/EmployeeJoin";
import EmployeeReset from "./employee/pages/EmployeeReset";
import EmployeeHome from "./employee/pages/EmployeeHome";
import EmployeeHistory from "./employee/pages/EmployeeHistory";
import EmployeeStubs from "./employee/pages/EmployeeStubs";
import OpsExpenses from "./ops/pages/OpsExpenses";
import OpsSubcontractorCosts from "./ops/pages/OpsSubcontractorCosts";
import OpsFinancials from "./ops/pages/OpsFinancials";
import OpsPriceBook from "./ops/pages/OpsPriceBook";
import OpsQuotes from "./ops/pages/OpsQuotes";
import OpsQuoteBuilder from "./ops/pages/OpsQuoteBuilder";
import OpsChangeOrders from "./ops/pages/OpsChangeOrders";
import OpsChangeOrderBuilder from "./ops/pages/OpsChangeOrderBuilder";
import OpsReports from "./ops/pages/OpsReports";
import OpsAssistant from "./ops/pages/OpsAssistant";
import OpsLeads from "./ops/pages/OpsLeads";
import PublicQuote from "./pages/PublicQuote";
import PublicChangeOrder from "./pages/PublicChangeOrder";
import { BarChart3, Settings } from "lucide-react";

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Jump instantly — smooth scroll here animates through the new page's
    // content on every route change, which looks like flashing/glitching.
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    html.style.scrollBehavior = prev;
  }, [pathname]);

  return null;
}

function App() {
  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith("/admin")) return;
    if (sessionStorage.getItem("bb_view_tracked")) return;
    sessionStorage.setItem("bb_view_tracked", "1");
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/track-view`).catch(() => {});
  }, []);

  return (
    <div className="App grain-texture">
      <BrowserRouter>
        <SiteSettingsProvider>
          <ScrollToTop />
          <Routes>
            {/* Public Routes with Navigation */}
            <Route path="/" element={<><Navigation /><Home /><Footer /></>} />
            <Route path="/about" element={<><Navigation /><About /><Footer /></>} />
            <Route path="/gallery" element={<><Navigation /><Gallery /><Footer /></>} />
            <Route path="/reviews" element={<><Navigation /><Reviews /><Footer /></>} />
            <Route path="/contact" element={<><Navigation /><Contact /><Footer /></>} />
            <Route path="/quote" element={<><Navigation /><Quote /><Footer /></>} />

            {/* Public Customer Quote (secure token, standalone branded page) */}
            <Route path="/q/:token" element={<PublicQuote />} />
            <Route path="/workforce" element={<WorkforceLanding />} />
            <Route path="/workforce/login" element={<EmployeeLogin />} />
            <Route path="/workforce/join/:token" element={<EmployeeJoin />} />
            <Route path="/workforce/reset/:token" element={<EmployeeReset />} />
            <Route path="/workforce/home" element={<EmployeeHome />} />
            <Route path="/workforce/history" element={<EmployeeHistory />} />
            <Route path="/workforce/pay" element={<EmployeeStubs />} />

            {/* Public Customer Change Order (secure token, standalone branded page) */}
            <Route path="/co/:token" element={<PublicChangeOrder />} />
            
            {/* Admin Routes (hidden, no navigation) */}
            <Route path="/admin" element={<Navigate to="/ops/login" replace />} />
            <Route path="/admin/dashboard" element={<Navigate to="/ops/website-admin" replace />} />

            {/* BuildBroz Ops (hidden internal app, protected by existing admin auth) */}
            <Route path="/ops/login" element={<OpsLogin />} />
            <Route path="/ops" element={<OpsAuthGuard><OpsLayout /></OpsAuthGuard>}>
              <Route index element={<OpsDashboard />} />
              <Route path="projects" element={<OpsProjects />} />
              <Route path="projects/:projectId" element={<OpsProjectWorkspace />} />
              <Route path="leads" element={<OpsLeads />} />
              <Route path="calendar" element={<OpsCalendar />} />
              <Route path="quotes" element={<OpsQuotes />} />
              <Route path="quotes/:quoteId" element={<OpsQuoteBuilder />} />
              <Route path="change-orders" element={<OpsChangeOrders />} />
              <Route path="change-orders/:coId" element={<OpsChangeOrderBuilder />} />
              <Route path="price-book" element={<OpsPriceBook />} />
              <Route path="expenses" element={<OpsExpenses />} />
              <Route path="employee-pay" element={<OpsEmployeePay />} />
              <Route path="subcontractor-costs" element={<OpsSubcontractorCosts />} />
              <Route path="financials" element={<OpsFinancials />} />
              <Route path="reports" element={<OpsReports />} />
              <Route path="assistant" element={<OpsAssistant />} />
              <Route path="website-admin" element={<AdminDashboard />} />
              <Route path="settings" element={<OpsPlaceholder title="Settings" icon={Settings} />} />
            </Route>
          </Routes>
          <Toaster />
        </SiteSettingsProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
